import { useState } from "react";
import { theme, card } from "../theme";

import { cbSafe } from "../lib/colourBlind";
// Public (unauthenticated) partner application form.
// No auth guard, no sidebar — similar pattern to SupplierQuestionnaire.tsx.

const API = import.meta.env.VITE_API_BASE_URL as string;

type Tier = "Starter" | "Growth" | "Enterprise";

interface FormState {
  firmName: string;
  contactName: string;
  contactEmail: string;
  website: string;
  description: string;
  expectedClients: string;
  requestedTier: Tier;
}

const INITIAL_FORM: FormState = {
  firmName: "",
  contactName: "",
  contactEmail: "",
  website: "",
  description: "",
  expectedClients: "",
  requestedTier: "Starter",
};

const TIER_COMPARISON: Array<{
  feature: string;
  starter: string;
  growth: string;
  enterprise: string;
}> = [
  { feature: "Client organisations",    starter: "Up to 10",   growth: "Up to 50",  enterprise: "Unlimited" },
  { feature: "AI compliance checks",    starter: "100/month",  growth: "500/month", enterprise: "Unlimited" },
  { feature: "White-label branding",    starter: "Yes",        growth: "Yes",       enterprise: "Yes" },
  { feature: "Dedicated account manager", starter: "—",        growth: "Yes",       enterprise: "Yes" },
  { feature: "Custom AI model",         starter: "—",          growth: "—",         enterprise: "Yes" },
];

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 3,
  fontSize: 13,
  border: "1px solid var(--c-border)",
  background: "var(--c-panel)",
  color: "var(--c-text)",
  boxSizing: "border-box",
  fontFamily: "inherit",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--c-muted)",
  marginBottom: 5,
};

const fieldWrap: React.CSSProperties = { marginBottom: 16 };

function FieldError({ msg }: { msg: string | undefined }) {
  if (!msg) return null;
  return <div style={{ fontSize: 11, color: "var(--c-dangerText)", marginTop: 4 }}>{msg}</div>;
}

export function PartnerApplyPage() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  function set(field: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.firmName.trim()) errs.firmName = "Firm name is required.";
    if (!form.contactName.trim()) errs.contactName = "Contact name is required.";
    if (!form.contactEmail.trim()) {
      errs.contactEmail = "Contact email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) {
      errs.contactEmail = "Please enter a valid email address.";
    }
    if (form.website && !/^https?:\/\/.+/.test(form.website)) {
      errs.website = "Website must start with https:// or http://";
    }
    if (!form.description.trim()) {
      errs.description = "Please describe your practice.";
    } else if (form.description.trim().length < 50) {
      errs.description = `Please write at least 50 characters (${form.description.trim().length} so far).`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await fetch(`${API}/partner/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firmName:        form.firmName.trim(),
          contactName:     form.contactName.trim(),
          contactEmail:    form.contactEmail.trim(),
          website:         form.website.trim() || undefined,
          description:     form.description.trim(),
          expectedClients: form.expectedClients ? parseInt(form.expectedClients, 10) : undefined,
          requestedTier:   form.requestedTier,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Submission failed — please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Submission failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", fontFamily: theme.font, background: "var(--c-bg)", padding: "60px 16px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ ...card, maxWidth: 540, width: "100%", padding: "36px 40px", textAlign: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: cbSafe("#dcfce7"), border: cbSafe("2px solid #86efac"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 18px" }}>
            ✓
          </div>
          <div style={{ fontSize: 19, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
            Application received
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
            Thank you for applying to the Abacas partner network. We'll be in touch within <strong>2 business days</strong>.
          </div>
          <div style={{ marginTop: 20, fontSize: 12, color: theme.color.muted }}>
            Questions? Email us at{" "}
            <a href="mailto:sales@abacassecurity.com" style={{ color: theme.color.accent }}>
              sales@abacassecurity.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", fontFamily: theme.font, background: "var(--c-bg)", padding: "48px 16px 60px", display: "flex", justifyContent: "center" }}>
      <div style={{ maxWidth: 680, width: "100%" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: theme.color.subtle, marginBottom: 8 }}>
            Partner Programme
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: theme.color.heading, letterSpacing: "-0.5px", marginBottom: 10 }}>
            Become an Abacas Partner
          </div>
          <p style={{ fontSize: 14, color: theme.color.subtle, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
            Join the Abacas partner network and offer world-class compliance management to your clients.
          </p>
        </div>

        {/* Form card */}
        <div style={{ ...card, padding: "32px 36px" }}>
          <form onSubmit={handleSubmit} noValidate>
            {/* Firm name */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Firm name *</label>
              <input
                type="text"
                value={form.firmName}
                onChange={(e) => set("firmName", e.target.value)}
                placeholder="Acme Compliance Ltd"
                style={{ ...inputStyle, borderColor: errors.firmName ? "var(--c-danger)" : undefined }}
                maxLength={200}
              />
              <FieldError msg={errors.firmName} />
            </div>

            {/* Contact name */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Contact name *</label>
              <input
                type="text"
                value={form.contactName}
                onChange={(e) => set("contactName", e.target.value)}
                placeholder="Jane Smith"
                style={{ ...inputStyle, borderColor: errors.contactName ? "var(--c-danger)" : undefined }}
                maxLength={200}
              />
              <FieldError msg={errors.contactName} />
            </div>

            {/* Contact email */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Contact email *</label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set("contactEmail", e.target.value)}
                placeholder="jane@acmecompliance.co.uk"
                style={{ ...inputStyle, borderColor: errors.contactEmail ? "var(--c-danger)" : undefined }}
                maxLength={320}
              />
              <FieldError msg={errors.contactEmail} />
            </div>

            {/* Website */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Website (optional)</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                placeholder="https://acmecompliance.co.uk"
                style={{ ...inputStyle, borderColor: errors.website ? "var(--c-danger)" : undefined }}
                maxLength={500}
              />
              <FieldError msg={errors.website} />
            </div>

            {/* Description */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Description of your practice *</label>
              <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 6 }}>
                Tell us about your firm and the clients you work with. Minimum 50 characters.
              </div>
              <textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="We are a cybersecurity and compliance consultancy specialising in…"
                rows={4}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  minHeight: 100,
                  borderColor: errors.description ? "var(--c-danger)" : undefined,
                }}
                maxLength={3000}
              />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                <FieldError msg={errors.description} />
                <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
                  {form.description.trim().length}/3000
                </span>
              </div>
            </div>

            {/* Expected clients */}
            <div style={fieldWrap}>
              <label style={labelStyle}>Expected number of clients (optional)</label>
              <input
                type="number"
                min={0}
                step={1}
                value={form.expectedClients}
                onChange={(e) => set("expectedClients", e.target.value)}
                placeholder="e.g. 15"
                style={{ ...inputStyle, width: 180 }}
              />
            </div>

            {/* Requested tier */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Requested tier *</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                {(["Starter", "Growth", "Enterprise"] as Tier[]).map((tier) => {
                  const active = form.requestedTier === tier;
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => set("requestedTier", tier)}
                      style={{
                        flex: 1,
                        padding: "10px 8px",
                        borderRadius: 3,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        textAlign: "center",
                        border: `1.5px solid ${active ? theme.color.accent : "var(--c-border)"}`,
                        background: active ? "var(--c-accentBg)" : "transparent",
                        color: active ? "var(--c-accentText)" : theme.color.muted,
                        transition: "border-color 0.1s, background 0.1s",
                      }}
                    >
                      {tier}
                    </button>
                  );
                })}
              </div>

              {/* Tier comparison table */}
              <div style={{ border: "1px solid var(--c-border)", borderRadius: 3, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "var(--c-panelAlt)" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: theme.color.muted, borderBottom: "1px solid var(--c-line)" }}>Feature</th>
                      {(["Starter", "Growth", "Enterprise"] as Tier[]).map((tier) => (
                        <th
                          key={tier}
                          style={{
                            padding: "8px 12px", textAlign: "center", fontWeight: 700,
                            color: form.requestedTier === tier ? "var(--c-accentText)" : theme.color.muted,
                            background: form.requestedTier === tier ? "var(--c-accentBg)" : undefined,
                            borderBottom: "1px solid var(--c-line)",
                          }}
                        >
                          {tier}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIER_COMPARISON.map((row, i) => (
                      <tr key={row.feature} style={{ borderBottom: i < TIER_COMPARISON.length - 1 ? "1px solid var(--c-border)" : "none" }}>
                        <td style={{ padding: "8px 12px", color: theme.color.text, fontWeight: 500 }}>{row.feature}</td>
                        {(["starter", "growth", "enterprise"] as const).map((key, j) => {
                          const tierName = (["Starter", "Growth", "Enterprise"] as Tier[])[j];
                          const isActive = form.requestedTier === tierName;
                          const val = row[key];
                          return (
                            <td
                              key={key}
                              style={{
                                padding: "8px 12px",
                                textAlign: "center",
                                background: isActive ? "var(--c-accentBg)" : undefined,
                                color: val === "—" ? theme.color.faint : val === "Yes" ? "var(--c-successText)" : theme.color.text,
                                fontWeight: val === "—" ? 400 : 600,
                              }}
                            >
                              {val}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Server error */}
            {serverError && (
              <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 12 }}>
                {serverError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "12px 20px",
                borderRadius: 3,
                fontSize: 14,
                fontWeight: 700,
                border: "none",
                background: submitting ? "var(--c-border)" : theme.color.accent,
                color: submitting ? theme.color.muted : "#fff",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
                transition: "opacity 0.15s",
              }}
            >
              {submitting ? "Submitting…" : "Submit Application"}
            </button>

            <div style={{ marginTop: 14, fontSize: 11, color: theme.color.faint, textAlign: "center" }}>
              By submitting you agree that Abacas Security may contact you about this application.
              Questions? Email{" "}
              <a href="mailto:sales@abacassecurity.com" style={{ color: theme.color.accent }}>
                sales@abacassecurity.com
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
